import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CommandeService } from 'src/app/core/services/commande.service';
import { SharedUtilsService } from 'src/app/core/services/shared-utils.service';
import { SseNotificationService } from 'src/app/core/services/sse-notification.service';
import { MessageService } from 'src/app/core/services/message.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { ExportCsvService } from 'src/app/core/services/export-csv.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-listecommandes',
  templateUrl: './liste-commandes.component.html',
  styleUrls: ['./liste-commandes.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ListeCommandesComponent implements OnInit, OnDestroy {

  nouvelleCommandeFlash = false; // animation flash sur la première ligne

  optionsTaillePage: number[] = [10, 25, 50, 100];
  taillePage: number = 10;
  totalEnregistrements: number = 0;
  //page: number = 0;
  indicateurChargement = true;

  sourceDonnees: any[] = [];
  commandesFiltrees: any[] = [];
  commandesPaginees: any[] = [];
  assignationEnCoursIds = new Set<number>();

  /** Panneau latéral d'affectation (même UX que la fiche commande). */
  panelAffectationOuvert = false;
  commandeCibleAffectation: any | null = null;
  rechercheLivreur = '';
  chargementLivreurs = false;
  livreursPanel: any[] = [];
  livreursPanelFiltres: any[] = [];
  /** Montant saisi pour les commandes à tarification particulière */
  montantContrat: number | null = null;

  termeRecherche: string = '';
  statutSelectionne: string = 'all';
  dateDebut: string = '';
  dateFin: string = '';

  // Incident modal
  modalIncidentOuvert = false;
  commandeIncident: any = null;
  incidentForm = { type: '', description: '' };
  enregistrementIncident = false;

  /** Compteurs globaux (base de données), alignés avec le dashboard — les anciennes méthodes ne comptaient que la page courante. */
  compteursGlobaux: Record<string, number> | null = null;

  colonneTri: string = '';
  directionTri: 'asc' | 'desc' = 'asc';

  pageCourante: number = 1;
  totalPages: number = 1;
  indexDebut: number = 1;
  indexFin: number = 10;

  private readonly destroy$ = new Subject<void>();
  private sseSub?: Subscription;
  private sseMajSub?: Subscription;

  constructor(
    public commandeService: CommandeService,
    public servicesPartages: SharedUtilsService,
    private router: Router,
    private toast: ToastService,
    private sse: SseNotificationService,
    private messageService: MessageService,
    private exportCsv: ExportCsvService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    // L'onglet « En attente » (EN_ATTENTE) a été retiré : éviter un filtre coincé sur cette valeur.
    if (this.statutSelectionne === 'EN_ATTENTE') {
      this.statutSelectionne = 'all';
    }
    this.souscrireDonneesPagination();
    this.commandeService.obtenirCommandes(0, Number(this.taillePage));
    this.ecouterNouvellesCommandes();
    this.ecouterMisesAJour();
  }

  private chargerCompteursGlobaux(): void {
    this.commandeService.obtenirCompteursListe().subscribe({
      next: (c) => {
        this.compteursGlobaux = c ?? null;
      },
      error: () => {
        this.compteursGlobaux = null;
      }
    });
  }

  /** Pastille « Toutes » : total en base (identique à la pagination sans filtre métier). */
  compteurToutesListe(): number {
    const v = this.compteursGlobaux?.['total'];
    if (v != null && v >= 0) {
      return v;
    }
    return this.totalEnregistrements || 0;
  }

  /** Aligné sur `countCommandesEnAttenteDeLivreur` côté API (même indicateur que le dashboard). */
  compteurSansLivreurListe(): number {
    const v = this.compteursGlobaux?.['sansLivreur'];
    if (v != null && v >= 0) {
      return v;
    }
    return this.compterSansLivreur();
  }

  compteurStatutListe(statut: string): number {
    const v = this.compteursGlobaux?.[statut];
    if (v != null && v >= 0) {
      return v;
    }
    return this.compterParStatut(statut);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.sseSub?.unsubscribe();
    this.sseMajSub?.unsubscribe();
  }

  private ecouterMisesAJour(): void {
    this.sseMajSub = this.sse.miseAJourCommande$.subscribe((maj) => {
      if (!maj?.commandeId) return;
      const index = this.sourceDonnees.findIndex(c => c.id === maj.commandeId);
      if (index !== -1) {
        const updated = { ...this.sourceDonnees[index] };
        if (maj.statut) updated.statut = maj.statut;
        if (maj.livreurId) updated.livreurId = maj.livreurId;
        if (maj.livreurNom) updated.livreurNom = maj.livreurNom;
        this.sourceDonnees = [
          ...this.sourceDonnees.slice(0, index),
          updated,
          ...this.sourceDonnees.slice(index + 1)
        ];
        this.appliquerFiltres();
      }
      this.chargerCompteursGlobaux();
    });
  }

  private ecouterNouvellesCommandes(): void {
    this.sseSub = this.sse.nouvelleCommande$.subscribe((commande) => {
      if (!commande) return;
      const existe = this.sourceDonnees.some(c => c.id === commande.id);
      if (!existe) {
        // Charger la commande complète pour avoir toutes les colonnes (client, typeEnvoi, transport…)
        this.commandeService.obtenirCommande(commande.id).subscribe({
          next: (commandeComplete: any) => {
            commandeComplete._nouveau = true;
            this.sourceDonnees = [commandeComplete, ...this.sourceDonnees];
            this.totalEnregistrements = this.totalEnregistrements + 1;
            this.appliquerFiltres();
            this.chargerCompteursGlobaux();
            this.nouvelleCommandeFlash = true;
            setTimeout(() => this.nouvelleCommandeFlash = false, 2000);
          },
          error: () => {
            // Fallback : insérer avec les données minimales SSE si l'API échoue
            this.sourceDonnees = [{ ...commande }, ...this.sourceDonnees];
            this.totalEnregistrements = this.totalEnregistrements + 1;
            this.appliquerFiltres();
            this.chargerCompteursGlobaux();
            this.nouvelleCommandeFlash = true;
            setTimeout(() => this.nouvelleCommandeFlash = false, 2000);
          }
        });
      }
    });
  }

  souscrireDonneesPagination() {
    this.commandeService.donnees$.pipe(takeUntil(this.destroy$)).subscribe({
      next: donnees => {
        this.sourceDonnees = donnees;
        this.appliquerFiltres();
        this.chargerCompteursGlobaux();
      },
      error: erreur => console.error('Erreur lors de la récupération des données', erreur)
    });

    this.commandeService.chargement$.pipe(takeUntil(this.destroy$)).subscribe({
      next: donnees => {
        setTimeout(() => { this.indicateurChargement = donnees; }, 500);
      }
    });

    this.commandeService.totalElementsDonnees$.pipe(takeUntil(this.destroy$)).subscribe({
      next: donnees => {
        if (donnees && donnees > 0) {
          this.totalEnregistrements = donnees;
          this.appliquerFiltres();
        }
      }
    });
  }

  surChangementRecherche() {
    this.appliquerFiltres();
  }

  selectionnerStatut(statut: string) {
    this.statutSelectionne = statut;
    this.pageCourante = 1;
    this.commandeService.obtenirCommandes(0, Number(this.taillePage), statut);
  }

  compterParStatut(statut: string): number {
    return this.sourceDonnees.filter(cmd => cmd.statut === statut).length;
  }

  compterSansLivreur(): number {
    return this.sourceDonnees.filter(cmd => !cmd.livreurId || cmd.livreurId === null).length;
  }

  appliquerFiltres() {
    let resultat = [...this.sourceDonnees];

    if (this.termeRecherche) {
      const terme = this.termeRecherche.toLowerCase();
      resultat = resultat.filter(cmd =>
        (cmd.numeroCommande?.toLowerCase() || '').includes(terme) ||
        (cmd.numeroSuivi?.toLowerCase() || '').includes(terme) ||
        (cmd.clientNom?.toLowerCase() || '').includes(terme) ||
        (cmd.statut?.toLowerCase() || '').includes(terme) ||
        (cmd.typeEnvoi?.toLowerCase() || '').includes(terme)
      );
    }

    if (this.dateDebut) {
      const debut = new Date(this.dateDebut).getTime();
      resultat = resultat.filter(cmd => {
        const d = cmd.dateCreation || cmd.dateCommande;
        return d && new Date(d).getTime() >= debut;
      });
    }

    if (this.dateFin) {
      const fin = new Date(this.dateFin).getTime() + 86399999; // fin de journée
      resultat = resultat.filter(cmd => {
        const d = cmd.dateCreation || cmd.dateCommande;
        return d && new Date(d).getTime() <= fin;
      });
    }

    this.commandesFiltrees = resultat;
    this.mettreAJourPagination();
  }

  reinitialiserDates(): void {
    this.dateDebut = '';
    this.dateFin = '';
    this.appliquerFiltres();
  }

  exporterCSV(): void {
    const colonnes = [
      { header: 'N° Commande', key: 'numeroCommande' },
      { header: 'Date', key: '_dateFormatee' },
      { header: 'Client', key: 'clientNom' },
      { header: 'Livreur', key: 'livreurNom' },
      { header: 'Statut', key: '_statutTexte' },
      { header: 'Montant', key: 'montantTotal' },
      { header: 'Type envoi', key: 'typeEnvoi' },
    ];
    const donnees = this.commandesFiltrees.map(cmd => ({
      ...cmd,
      _dateFormatee: cmd.dateCreation ? new Date(cmd.dateCreation).toLocaleDateString('fr-FR') : '—',
      _statutTexte: this.obtenirTexteStatut(cmd.statut),
    }));
    this.exportCsv.exporter('commandes', colonnes, donnees);
  }

  ouvrirModalIncident(commande: any): void {
    this.commandeIncident = commande;
    this.incidentForm = { type: '', description: '' };
    this.modalIncidentOuvert = true;
  }

  fermerModalIncident(): void {
    this.modalIncidentOuvert = false;
    this.commandeIncident = null;
  }

  soumettreIncident(): void {
    if (!this.incidentForm.type || !this.commandeIncident) return;
    this.enregistrementIncident = true;
    const payload = {
      commandeId: this.commandeIncident.id,
      type: this.incidentForm.type,
      description: this.incidentForm.description,
      statut: 'OUVERT',
    };
    this.http.post(`${environment.apiurl}incidents/enregistrerIncident`, payload).subscribe({
      next: () => {
        this.toast.succes('Incident signalé avec succès');
        this.enregistrementIncident = false;
        this.fermerModalIncident();
      },
      error: () => {
        this.toast.erreur('Erreur lors de la création de l\'incident');
        this.enregistrementIncident = false;
      }
    });
  }

  surTri(colonne: string) {
    if (this.colonneTri === colonne) {
      this.directionTri = this.directionTri === 'asc' ? 'desc' : 'asc';
    } else {
      this.colonneTri = colonne;
      this.directionTri = 'asc';
    }

    this.commandesFiltrees.sort((a, b) => {
      const valeurA = a[colonne] || '';
      const valeurB = b[colonne] || '';

      if (valeurA < valeurB) return this.directionTri === 'asc' ? -1 : 1;
      if (valeurA > valeurB) return this.directionTri === 'asc' ? 1 : -1;
      return 0;
    });

    this.mettreAJourPagination();
  }

  surChangementTaillePage() {
    this.pageCourante = 1;
    this.commandeService.obtenirCommandes(0, Number(this.taillePage), this.statutSelectionne);
    this.mettreAJourPagination();
  }

  allerALaPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.pageCourante = page;
      this.commandeService.obtenirCommandes(this.pageCourante - 1, Number(this.taillePage), this.statutSelectionne);
    }
  }

  obtenirNumerosPages(): number[] {
    const pages: number[] = [];
    const maxPages = 5;
    let pageDebut = Math.max(1, this.pageCourante - Math.floor(maxPages / 2));
    let pageFin = Math.min(this.totalPages, pageDebut + maxPages - 1);

    if (pageFin - pageDebut < maxPages - 1) {
      pageDebut = Math.max(1, pageFin - maxPages + 1);
    }

    for (let i = pageDebut; i <= pageFin; i++) {
      pages.push(i);
    }

    return pages;
  }

  mettreAJourPagination() {
    this.totalPages = Math.ceil(this.totalEnregistrements / Number(this.taillePage));
    this.indexDebut = (this.pageCourante - 1) * Number(this.taillePage) + 1;
    this.indexFin = Math.min(this.pageCourante * Number(this.taillePage), this.totalEnregistrements);

    /* const debut = (this.pageCourante - 1) * Number(this.taillePage);
    const fin = debut + Number(this.taillePage);
    this.commandesPaginees = this.commandesFiltrees.slice(debut, fin); */
    this.commandesPaginees = this.commandesFiltrees;
  }

  obtenirClasseBadgeStatut(statut: string): string {
    const correspondanceStatut: { [cle: string]: string } = {
      'EN_ATTENTE': 'status-pending',
      'EN_PREPARATION': 'status-preparation',
      'AFFECTE': 'status-assigned',
      'EN_LIVRAISON': 'status-delivery',
      'LIVRE': 'status-delivered',
      'ANNULE': 'status-cancelled'
    };
    return correspondanceStatut[statut] || 'status-pending';
  }

  obtenirTexteStatut(statut: string): string {
    const correspondanceTexte: { [cle: string]: string } = {
      'EN_ATTENTE': 'Commande enregistrée',
      'EN_PREPARATION': 'En route vers le retrait',
      'AFFECTE': 'Livreur assigné',
      'EN_LIVRAISON': 'Colis en route',
      'LIVRE': 'Livrée',
      'ANNULE': 'Annulée'
    };
    return correspondanceTexte[statut] || statut;
  }

  voirDetails(commande: any) {
    this.router.navigate(['/commande', commande.id]);
  }

  modifierCommande(commande: any) {
    this.router.navigate(['/commande/enregistrer', commande.id]);
  }

  datePassage(commande: any): string | null {
    return commande?.dateCreation || commande?.dateCommande || null;
  }

  /** Sous-titre sous le client : livreur assigné ou indication « sans livreur » pour les commandes actives. */
  libelleLivreurListe(commande: any): string {
    const n = `${commande?.livreurNom ?? ''}`.trim();
    if (n) return n;
    if (commande?.statut === 'LIVRE' || commande?.statut === 'ANNULE') return '';
    return 'Sans livreur';
  }

  estAssignationEnCours(idCommande: number): boolean {
    return this.assignationEnCoursIds.has(idCommande);
  }

  libelleTypeVehicule(livreur: any): string {
    const type = livreur?.vehiculeActuel?.typeVehicule;
    const labels: Record<string, string> = {
      MOTO: '🏍️ Moto',
      CAMION_LEGER: '🚐 Camion léger',
      GROS_CAMION: '🚛 Gros camion',
      CAMION: '🚛 Camion',
      VELO: '🚲 Vélo',
    };
    return labels[type] ?? (type ? type : '');
  }

  assignerLivreur(commande: any) {
    if (!commande?.id || this.assignationEnCoursIds.has(commande.id)) return;
    this.commandeCibleAffectation = commande;
    this.ouvrirPanelAffectationLivreur();
  }

  ouvrirPanelAffectationLivreur(): void {
    const idCommande = this.commandeCibleAffectation?.id;
    if (!idCommande) {
      this.toast.erreur('Commande introuvable pour charger les livreurs.');
      return;
    }
    this.panelAffectationOuvert = true;
    this.rechercheLivreur = '';
    this.chargementLivreurs = true;
    this.livreursPanel = [];
    this.livreursPanelFiltres = [];
    this.montantContrat = null;

    this.servicesPartages.obtenirLivreursDisponibles(idCommande).subscribe({
      next: (reponse: any) => {
        const liste = Array.isArray(reponse) ? reponse : (reponse?.content ?? []);
        this.livreursPanel = liste;
        this.livreursPanelFiltres = [...liste];
        this.chargementLivreurs = false;
      },
      error: (erreur) => {
        this.chargementLivreurs = false;
        this.livreursPanel = [];
        this.livreursPanelFiltres = [];
        this.toast.erreur('Impossible de charger les livreurs : ' + this.messageService.afficherMessageErreur(erreur));
      }
    });
  }

  fermerPanelAffectation(): void {
    const id = this.commandeCibleAffectation?.id;
    if (id != null && this.assignationEnCoursIds.has(id)) return;
    this.panelAffectationOuvert = false;
    this.commandeCibleAffectation = null;
  }

  filtrerLivreursPanel(): void {
    const terme = (this.rechercheLivreur || '').trim().toLowerCase();
    if (!terme) {
      this.livreursPanelFiltres = [...this.livreursPanel];
      return;
    }
    this.livreursPanelFiltres = this.livreursPanel.filter((livreur: any) =>
      `${livreur?.nom || ''} ${livreur?.prenom || ''}`.toLowerCase().includes(terme) ||
      `${livreur?.contact || ''}`.toLowerCase().includes(terme) ||
      `${livreur?.adresse || ''}`.toLowerCase().includes(terme)
    );
  }

  assignerLivreurDepuisPanel(livreurChoisi: any): void {
    const idCommande = this.commandeCibleAffectation?.id;
    if (!idCommande || !livreurChoisi?.id || this.assignationEnCoursIds.has(idCommande)) return;

    const estContrat = this.commandeCibleAffectation?.flgTarificationParticuliere === true;
    if (estContrat && (this.montantContrat == null || this.montantContrat < 0)) {
      this.toast.erreur('Veuillez saisir le montant à facturer pour cette commande sous contrat.', 'Montant requis');
      return;
    }

    this.assignationEnCoursIds.add(idCommande);
    this.sse.ignorerProchainToastAffectationLivreur(idCommande);
    const montant = estContrat ? this.montantContrat! : undefined;
    this.servicesPartages.attribuerLivreurManuellement(idCommande, livreurChoisi.id, montant).subscribe({
      next: (donnees: any) => {
        this.assignationEnCoursIds.delete(idCommande);
        this.toast.succes(`Le livreur ${donnees.livreurNom} a été assigné sur la commande`, 'Assignation du livreur');
        this.panelAffectationOuvert = false;
        this.commandeCibleAffectation = null;
        this.commandeService.obtenirCommandes(this.pageCourante - 1, Number(this.taillePage), this.statutSelectionne);
      },
      error: (erreur) => {
        this.sse.ignorerProchainToastAffectationLivreur(idCommande);
        this.commandeService.obtenirCommande(idCommande).subscribe({
          next: (commandeFraiche: any) => {
            this.assignationEnCoursIds.delete(idCommande);
            if (commandeFraiche?.livreurId || commandeFraiche?.livreurNom) {
              this.sse.ignorerProchainToastAffectationLivreur(idCommande);
              this.toast.succes('Livreur affecté avec succès.', 'Assignation du livreur');
              this.panelAffectationOuvert = false;
              this.commandeCibleAffectation = null;
              this.commandeService.obtenirCommandes(this.pageCourante - 1, Number(this.taillePage), this.statutSelectionne);
              return;
            }
            console.error('Erreur lors de l\'assignation', erreur);
            this.toast.erreur('Erreur lors de l\'affectation : ' + this.messageService.afficherMessageErreur(erreur));
          },
          error: () => {
            this.assignationEnCoursIds.delete(idCommande);
            console.error('Erreur lors de l\'assignation', erreur);
            this.toast.erreur('Erreur lors de l\'affectation : ' + this.messageService.afficherMessageErreur(erreur));
          }
        });
      }
    });
  }

  supprimerCommande(commande: any) {
    this.toast.confirmer(
      `Êtes-vous sûr de vouloir supprimer la commande ${commande.numeroCommande} ?`,
      'Confirmer la suppression',
      'Supprimer',
      'Annuler'
    ).subscribe(confirme => {
      if (confirme) {
        this.servicesPartages.supprimerCommande(commande.id).subscribe(
          () => {
            this.toast.succes('La commande a été supprimée avec succès', 'Suppression réussie');
            this.commandeService.obtenirCommandes(0, Number(this.taillePage), this.statutSelectionne);
            this.chargerCompteursGlobaux();
          },
          erreur => {
            console.error('Erreur lors de la suppression', erreur);
            this.toast.erreur('Une erreur est survenue lors de la suppression de la commande', 'Erreur');
          }
        );
      }
    });
  }
}
